import React, { useEffect, useState } from 'react';
import { getCustomers, updateCustomer } from './api';
import jalaali from 'jalaali-js';
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

// MUI
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Modal,
  TextField,
  Pagination,
} from '@mui/material';

export default function CustomerList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const rowsPerPage = 10;

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await getCustomers();
      setRows(data);
    } catch (e) {
      console.error(e);
      alert('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }

  function toJalaliDate(dateStr) {
    if (!dateStr) return '';
    const g = new Date(dateStr);
    if (isNaN(g)) return dateStr;
    const j = jalaali.toJalaali(g);
    return `${j.jy}-${String(j.jm).padStart(2, '0')}-${String(j.jd).padStart(2, '0')}`;
  }

  async function handleSave() {
    setSaving(true);
    setSuccessMsg('');
    try {
      const res = await updateCustomer(editing);
      if (res.success) {
        setSuccessMsg('بروزرسانی با موفقیت انجام شد ✅');
        setTimeout(() => {
          setEditing(null);
          load();
        }, 1000);
      } else {
        alert(res.message || 'خطا در بروزرسانی');
      }
    } catch {
      alert('Server error');
    } finally {
      setSaving(false);
    }
  }

  // Filter + paginate
  const filteredRows = rows.filter(
    (r) =>
      r.person_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.customerCode?.toLowerCase().includes(search.toLowerCase())
  );
  const paginatedRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Box sx={{ p: 2, direction: 'rtl' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        محصولات با گارانتی فعال شده
      </Typography>

      {/* ✅ Compact RTL Search Input */}
      <Box sx={{ mb: 2, textAlign: 'right' }}>
        <input
          type="text"
          placeholder="🔍 جستجو بر اساس نام مشتری یا کد مشتری..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '8px 12px',
            width: '280px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            direction: 'rtl',
            outline: 'none',
          }}
        />
      </Box>

      {loading ? (
        <Typography>در حال بارگیری...</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                {[
                  '#',
                  'نام مشتری',
                  'کد مشتری',
                  'نام محصول',
                  'شماره سریال محصول',
                  'تاریخ خرید (شمسی)',
                  'خرید از',
                  'تلفن',
                  'ایمیل',
                  'آدرس',
                  'شهر',
                  'تاریخ فعال‌سازی (شمسی)',
                  'ویرایش',
                ].map((head, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      textAlign: 'center',
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((r, i) => (
                <TableRow
                  key={r.id}
                  sx={{
                    backgroundColor: i % 2 === 0 ? '#f7f7f7' : '#ffffff',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      transition: '0.2s',
                    },
                  }}
                >
                  <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                  <TableCell>{r.person_name}</TableCell>
                  <TableCell>{r.customerCode}</TableCell>
                  <TableCell>{r.productName}</TableCell>
                  <TableCell>{r.product_serial_number}</TableCell>
                  <TableCell>{r.buyDate}</TableCell>
                  <TableCell>{r.buyFrom}</TableCell>
                  <TableCell>{r.phone_number}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.address}</TableCell>
                  <TableCell>{r.city}</TableCell>
                  <TableCell>{toJalaliDate(r.warranty_activated_at)}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setEditing({ ...r })}
                    >
                      ✏️
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={Math.ceil(filteredRows.length / rowsPerPage)}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          shape="rounded"
        />
      </Box>

      {/* Edit Modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)}>
        <Box
          sx={{
            backgroundColor: 'white',
            p: 3,
            borderRadius: 2,
            maxWidth: 650,
            mx: 'auto',
            mt: 5,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            ویرایش اطلاعات مشتری
          </Typography>

          {[
            { label: 'نام مشتری', key: 'person_name' },
            { label: 'کد مشتری', key: 'customerCode' },
            { label: 'نام محصول', key: 'productName' },
            { label: 'خرید از', key: 'buyFrom' },
            { label: 'ایمیل', key: 'email', type: 'email' },
            { label: 'تلفن', key: 'phone_number' },
            { label: 'آدرس', key: 'address' },
            { label: 'شهر', key: 'city' },
          ].map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              fullWidth
              sx={{ mb: 2 }}
              value={editing?.[field.key] || ''}
              type={field.type || 'text'}
              onChange={(e) =>
                setEditing({ ...editing, [field.key]: e.target.value })
              }
            />
          ))}

          {/* Jalali DatePicker */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              تاریخ خرید
            </Typography>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              value={editing?.buyDate}
              onChange={(date) =>
                setEditing({ ...editing, buyDate: date?.format('YYYY-MM-DD') })
              }
              format="YYYY-MM-DD"
              style={{ width: '100%', direction: 'rtl' }}
            />
          </Box>

          {successMsg && (
            <Typography color="green" sx={{ mb: 1 }}>
              {successMsg}
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => setEditing(null)}>
              انصراف
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
